import React from 'react';
import {
	Grid,
	Typography,
	CircularProgress,
	IconButton as IconBtn,
	TextField,
	Box,
	List,
	ListItem,
	ListItemText,
	Button,
	Card,
	CardContent,
	Chip,
	Stack,
} from '@mui/material';
import { CircleClose } from '../../../assets/icons/Icons';
import { FileUploadDiv, helperText } from '../../../assets/theme/Theme';
import {
	border,
	dark,
	MAIN_THEME_COLOR,
	teal,
} from '../../../assets/theme/Colors';
import Dropzone from 'react-dropzone';
import FileContentTile from '../../../components/FileContentTile';
import {
	AutoAwesome,
	Check,
	Close,
	Download,
	Edit,
	Favorite,
	Save,
} from '@mui/icons-material';

function FileItem({ file, onRemoveFiles, isLoading }) {
	return (
		<Grid
			container
			alignItems="center"
			justifyContent="space-between"
			wrap="nowrap"
			sx={{
				border: '1px solid #D9D9D9',
				borderRadius: '8px',
				padding: '6px 10px',
				maxWidth: '220px',
				backgroundColor: '#FAFAFA',
			}}>
			<Grid
				item
				xs
				sx={{
					overflow: 'hidden',
				}}>
				<Typography fontSize="13px" noWrap title={file?.name}>
					{file?.name}
				</Typography>
			</Grid>
			<Grid item>
				<IconBtn
					disableRipple
					style={{ padding: 0, marginLeft: '6px' }}
					disabled={isLoading}
					onClick={() => onRemoveFiles(file)}>
					<CircleClose />
				</IconBtn>
			</Grid>
		</Grid>
	);
}

function AttachInstructionFile({
	loading,
	readTextFile,
	inputError,
	selectedFiles,
	onRemoveFiles,
}) {
	return (
		<Grid container alignItems="center" spacing={2} wrap="nowrap">
			<Grid item xs>
				{selectedFiles && selectedFiles.length > 0 && (
					<Grid container spacing={2}>
						{selectedFiles.map((item, index) => (
							<Grid item key={index}>
								<FileItem
									file={item}
									onRemoveFiles={onRemoveFiles}
									isLoading={loading}
								/>
							</Grid>
						))}
					</Grid>
				)}
			</Grid>
			<Grid item>
				{!loading && (
					<Dropzone
						disabled={loading}
						onDrop={acceptedFiles => readTextFile(acceptedFiles)}>
						{({ getRootProps, getInputProps }) => (
							<div
								{...getRootProps()}
								style={{
									padding: '6px 10px',
									borderRadius: '5px',
									whiteSpace: 'nowrap',
									cursor: 'pointer',
								}}>
								<input {...getInputProps()} />
								{/* <Typography
									fontSize="14px"
									fontWeight={'600'}
									color={'#00b0a0'}>
									+ Attach files
								</Typography> */}
							</div>
						)}
					</Dropzone>
				)}
			</Grid>
		</Grid>
	);
}

function GeneratedPlanTemplate({
	plan,
	feedbacks,
	onPlanChange,
	onChangeFeedback,
	onAddFeedBackToPlan,
	isSubPlanLoading,
	feedbackError,
	feedbackFiles,
	onChangeFeedbackFile,
	onRemoveFeedbackFiles,
	handleClose,
	editingSection,
	handleTextChange,
	residentName,
	isLoading,
	onDownloadFile,
	onSaveToProfile,
	totalUpdates,
}) {
	function checkSubPlanLoading(index, itemIndex) {
		if (
			isSubPlanLoading.loading &&
			isSubPlanLoading.planIndex === index &&
			isSubPlanLoading.subPlanIndex === itemIndex
		) {
			return true;
		} else {
			return false;
		}
	}

	return (
		<Card
			sx={{
				borderRadius: '16px',
				border: '1px solid #d8e8e8',
				boxShadow: 'none',
				mt: 2,
			}}>
			<CardContent sx={{ p: '20px !important' }}>
				{/* HEADER */}
				<Box
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					mb={2}>
					<Box display="flex" alignItems="center" gap={1}>
						<Favorite sx={{ color: teal, fontSize: 20 }} />

						<Typography
							sx={{
								fontSize: 14,
								fontWeight: 700,
								color: dark,
							}}>
							Full Care Plan - {residentName || 'N/A'}
						</Typography>

						<Chip
							icon={<AutoAwesome sx={{ fontSize: 14 }} />}
							label={`${totalUpdates} AI updates highlighted`}
							size="small"
							sx={{
								height: 24,
								bgcolor: dark,
								color: teal,
								fontSize: 11,
								fontWeight: 600,
							}}
						/>
					</Box>

					<Button
						size="small"
						variant="outlined"
						startIcon={<Close fontSize="small" />}
						onClick={handleClose}
						sx={{
							textTransform: 'none',
							borderRadius: '8px',
							fontSize: 12,
							borderColor: border,
							color: '#1a2424',
						}}>
						Close
					</Button>
				</Box>

				{/* INFO BOX */}
				<Box
					sx={{
						background: '#e6f9f7',
						border: '1px solid #b3ede8',
						borderRadius: '8px',
						p: '10px 14px',
						mb: 2,
						fontSize: 12,
						color: '#0a3a30',
						display: 'flex',
						alignItems: 'center',
						gap: 1,
					}}>
					<AutoAwesome sx={{ color: teal, fontSize: 16 }} />

					<Typography sx={{ fontSize: 12 }}>
						<b>Highlighted in green</b> = AI-suggested additions or
						changes. All other text is from the existing plan.
					</Typography>
				</Box>

				{/* SECTION COMPONENT */}
				{plan.map((section, sectionIndex) => (
					<Box
						key={section.title}
						sx={{
							border: '1px solid #d8e8e8',
							borderRadius: '12px',
							overflow: 'hidden',
							mb: 1.5,
						}}>
						{/* SECTION HEADER */}
						<Box
							sx={{
								background: '#e6f9f7',
								borderBottom: '1px solid #b3ede8',
								p: '12px 16px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
								}}>
								<Typography
									sx={{
										fontSize: 13,
										fontWeight: 700,
										color: '#0a5050',
										mr: 1,
									}}>
									{section.title}
								</Typography>
								{section.modified && (
									<Chip
										icon={
											<AutoAwesome
												sx={{ fontSize: 10 }}
											/>
										}
										label={`Section modified`}
										size="small"
										sx={{
											height: 24,
											bgcolor: dark,
											color: teal,
											fontSize: 11,
											fontWeight: 600,
										}}
									/>
								)}
							</Box>

							<Button
								size="small"
								variant="outlined"
								startIcon={
									<Edit
										fontSize="small"
										sx={{ width: 12, height: 12 }}
									/>
								}
								onClick={() =>
									onPlanChange(
										editingSection === sectionIndex
											? null
											: sectionIndex,
									)
								}
								sx={{
									minWidth: 0,
									height: 26,
									fontSize: 11,
									textTransform: 'none',
									borderRadius: '6px',
									borderColor: border,
									color: '#1a2424',
									background: '#fff',
								}}>
								{editingSection === sectionIndex
									? 'Done'
									: 'Edit'}
							</Button>
						</Box>

						{/* BODY */}
						<Box sx={{ p: '16px 16px 0px 16px' }}>
							{section.bodyContent.map((item, itemIndex) => (
								<Box key={itemIndex} sx={{ mb: 2 }}>
									{/* SUB TITLE */}
									{item.label && (
										<Typography
											sx={{
												fontSize: 12,
												fontWeight: 700,
												color: '#0a5050',
												mb: 1,
												textTransform: 'uppercase',
											}}>
											{item.label}
										</Typography>
									)}

									{/* CONTENT */}
									{(item.content || []).map(
										(content, contentIndex) => (
											<Box
												key={contentIndex}
												sx={{
													display: 'flex',
													alignItems: 'flex-start',
													gap: 1,
													mb: 1,
													background: content.ai
														? '#e6f9f7'
														: 'transparent',
													borderRadius: content.ai
														? '6px'
														: 0,
													p: content.ai
														? '6px 10px'
														: 0,
												}}>
												{content.ai ? (
													<AutoAwesome
														sx={{
															fontSize: 15,
															color: '#0a5050',
															mt: '7px',
														}}
													/>
												) : (
													<Check
														sx={{
															fontSize: 15,
															color: teal,
															mt: '8px',
														}}
													/>
												)}
												{content.ai && (
													<Typography
														sx={{
															fontSize: 13,
															fontWeight: 700,
															color: '#0a3a30',

															whiteSpace:
																'nowrap',
															mt: '5px',
														}}>
														{content.type === 'new'
															? 'New:'
															: 'Updated:'}
													</Typography>
												)}

												{editingSection ===
												sectionIndex ? (
													<TextField
														fullWidth
														multiline
														variant="outlined"
														value={content.text}
														onChange={e =>
															handleTextChange(
																sectionIndex,
																itemIndex,
																contentIndex,
																e.target.value,
															)
														}
														sx={{
															'& .MuiOutlinedInput-root':
																{
																	fontSize:
																		'13px',
																	borderRadius:
																		'8px',
																	background:
																		'#fff',
																},
														}}
													/>
												) : (
													<Typography
														sx={{
															fontSize: 13,
															color: '#6b8888',
															lineHeight: 1.5,
															pt: '6px',
														}}>
														{content.text}
													</Typography>
												)}
											</Box>
										),
									)}
								</Box>
							))}
						</Box>
						<Grid
							container
							paddingX={'15px'}
							paddingBottom={'15px'}>
							<Typography
								sx={{
									fontSize: 12,
									fontWeight: 500,
									color: '#6b8888',
									mb: 1,
								}}>
								Add additional context
							</Typography>
							<Grid
								border={
									section.enableUserPrompt ||
									(feedbackError.itemIndex === sectionIndex &&
										feedbackError.status)
										? '1px solid red'
										: '1px solid #D9D9D9'
								}
								bgcolor={'#FFFFFF'}
								borderRadius={'5px 0px 0px 5px'}
								marginX={'10px'}
								width={'100%'}>
								<TextField
									fullWidth
									size="small"
									multiline
									placeholder="e.g. Family reported Maggie was upset after GP visit..."
									sx={{
										'& .MuiInputBase-root': {
											alignItems: 'flex-start',
											borderColor: 'transparent',
										},
										'& .MuiInputBase-input': {
											fontSize: 13,
											'&::placeholder': {
												fontSize: 13,
											},
										},
										'& .MuiOutlinedInput-root': {
											alignItems: 'flex-start',

											'& fieldset': {
												border: 'none',
											},

											'&:hover fieldset': {
												border: 'none',
											},

											'&.Mui-focused fieldset': {
												border: 'none',
											},
										},
									}}
									onChange={e =>
										onChangeFeedback(
											e.target.value,
											sectionIndex,
										)
									}
									value={feedbacks[sectionIndex]}
									minRows={3}
									maxRows={6}
									InputProps={{
										readOnly:
											checkSubPlanLoading(sectionIndex),
									}}
								/>
								<Grid
									container
									alignItems="center"
									justifyContent="space-between"
									spacing={1}
									paddingBottom={'5px'}
									paddingX={'5px'}>
									<Grid item xs>
										<AttachInstructionFile
											inputError={feedbackError}
											loading={checkSubPlanLoading(
												sectionIndex,
											)}
											readTextFile={e =>
												onChangeFeedbackFile(
													e,
													sectionIndex,
												)
											}
											selectedFiles={
												feedbackFiles[sectionIndex]
											}
											onRemoveFiles={value =>
												onRemoveFeedbackFiles(
													value,
													sectionIndex,
												)
											}
										/>
									</Grid>
									<Grid item>
										<Grid
											container
											alignItems="center"
											spacing={1}>
											{checkSubPlanLoading(
												sectionIndex,
											) && (
												<Grid
													paddingRight={'5px'}
													paddingTop={'13px'}>
													<CircularProgress
														sx={{
															color: '#00b0a0',
														}}
														size={'1.5rem'}
													/>
												</Grid>
											)}
											<Grid item>
												<Button
													onClick={() =>
														onAddFeedBackToPlan(
															sectionIndex,
															section.enableUserPrompt,
														)
													}
													disabled={checkSubPlanLoading(
														sectionIndex,
													)}
													sx={{
														textTransform: 'none',
														borderRadius: '8px',
														background: teal,
														color: dark,
														fontWeight: 600,
														boxShadow: 'none',
														'&:hover': {
															background:
																'#00b0a0',
															boxShadow: 'none',
														},
														fontSize: 13,
														justifyContent:
															'center',
													}}>
													Add
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
							<Grid
								container
								width={'100%'}
								justifyContent={'flex-end'}
								alignItems={'center'}
								paddingBottom={'10px'}
								paddingRight={'10px'}>
								{feedbackError.itemIndex === sectionIndex &&
									feedbackError.status && (
										<Typography
											marginRight={'10px'}
											style={helperText}>
											{feedbackError.msg}
										</Typography>
									)}
							</Grid>
						</Grid>
					</Box>
				))}

				{plan.length === 0 && (
					<Card
						sx={{
							borderRadius: '18px',
							border: '1px solid #d8e8e8',
							boxShadow: 'none',
							alignItems: 'center',
						}}>
						<CardContent
							sx={{
								p: 3,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								minHeight: 300,
								color: '#6b8888',
								fontSize: 13,
								fontWeight: 500,
							}}>
							No plan generated
						</CardContent>
					</Card>
				)}

				{/* FOOTER BUTTONS */}
				<Stack direction="row" spacing={1} mt={2}>
					<Button
						onClick={onSaveToProfile}
						startIcon={<Save />}
						disabled={isLoading}
						sx={{
							textTransform: 'none',
							borderRadius: '8px',
							background: teal,
							color: dark,
							fontWeight: 600,
							boxShadow: 'none',
							'&:hover': {
								background: '#00b0a0',
								boxShadow: 'none',
							},
							fontSize: 13,
							justifyContent: 'center',
						}}>
						Save Full Plan to Profile
					</Button>

					<Button
						variant="outlined"
						onClick={onDownloadFile}
						disabled={isLoading}
						startIcon={<Download />}
						sx={{
							textTransform: 'none',
							borderRadius: '8px',
							borderColor: border,
							color: '#1a2424',
							fontSize: 13,
						}}>
						Download PDF
					</Button>

					<Button
						variant="outlined"
						startIcon={<Close />}
						onClick={handleClose}
						sx={{
							textTransform: 'none',
							borderRadius: '8px',
							borderColor: border,
							color: '#1a2424',
							fontSize: 13,
						}}>
						Close
					</Button>
				</Stack>
			</CardContent>
		</Card>
	);
}

export default GeneratedPlanTemplate;
